import React, { useMemo, useState } from "react";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { getActivityTimeline } from "@/modules/lead-page/api/lead-page.v2.api";
import {
  ActivityResponseDto,
  ActivityType,
} from "@/modules/lead-page/dtos/index-v2";
import { useParams } from "react-router-dom";
import { ActivityContext } from "@/modules/lead-page/new/hooks/activity-context.hook";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import useDeleteComments from "../../hooks/comments/delete-comments.hook";
import { queryClient } from "@/queryClient";

interface ActivityFiltersState {
  includeChildLead?: boolean;
  type?: ActivityType[];
  setIncludeChildLead?: React.Dispatch<React.SetStateAction<boolean>>;
  setType?: (value: ActivityType[]) => void;
}

export interface ActivityContextType {
  filters?: ActivityFiltersState;
  data: InfiniteData<ActivityResponseDto> | undefined;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchPreviousPage?: () => void;
  hasPreviousPage?: boolean;
  isFetchingPreviousPage?: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  forceRefetch: () => void;
  handleDeleteComments: () => void;
  setDialogOpenConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  dialogOpenConfirm: boolean;
  loadDeleting?: boolean;
  deletingId?: number | string;
  setDeletingId: React.Dispatch<
    React.SetStateAction<number | string | undefined>
  >;
  highlightedActivityId?: string | undefined;
  setHighlightedActivityId?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  queryKey?: unknown[];
  isFindingActivity?: boolean;
  setIsFindingActivity?: React.Dispatch<React.SetStateAction<boolean>>;
  findInActivityRoute?: boolean;
  setFindInActivityRoute?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ActivityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isMobile } = useScreenSize();

  const { lead_id } = useParams<{ lead_id: string }>();

  const [includeChildLead, setIncludeChildLead] = useState(false);
  const [type, setType] = useState<ActivityType[]>([
    ActivityType.Comment,
    ActivityType.StatusUpdate,
    ActivityType.AssignmentUpdate,
    ActivityType.Rating,
    ActivityType.ZoomCall,
    ActivityType.WhatsappConversation,
  ]);

  const [mobileQueryVersion, setMobileQueryVersion] = useState(0);
  const [highlightedActivityId, setHighlightedActivityId] = useState<
    string | undefined
  >();
  const [isFindingActivity, setIsFindingActivity] = useState(false);
  const [findInActivityRoute, setFindInActivityRoute] = useState(false);

  // Use a ref to store the queryKey to prevent unnecessary re-fetches
  // but we need to update it when dependencies change
  const queryKey = useMemo(
    () =>
      isMobile
        ? ["lead-activity", lead_id, mobileQueryVersion]
        : ["lead-activity", lead_id, includeChildLead, type],
    [lead_id, includeChildLead, type, isMobile, mobileQueryVersion]
  );

  // Reset highlighted activity when filters change (user is manually changing view)
  React.useEffect(() => {
    if (highlightedActivityId) {
      setHighlightedActivityId(undefined);
    }
    // Also clear the find in activity route flag when filters change
    if (findInActivityRoute) {
      setFindInActivityRoute(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeChildLead, type]);

  const forceRefetch = () => {
    if (isMobile) {
      setMobileQueryVersion((v) => v + 1);
    } else {
      refetch();
    }
  };

  const {
    data,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage = false,
    isLoading = false,
    isError = false,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) =>
      getActivityTimeline(lead_id!, {
        limit: 20,
        type,
        includeChildLead,
        cursor: pageParam,
        direction: "desc", // Default to desc for loading older records
      }),
    getNextPageParam: (lastPage) =>
      lastPage?.data?.hasNext ? lastPage.data.nextCursor : undefined,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const hasPreviousPage = data?.pages[0]?.data?.hasPrev ?? false;
  const [isFetchingPreviousPage, setIsFetchingPreviousPage] = useState(false);

  const fetchPreviousPage = React.useCallback(async () => {
    if (!lead_id || !data || isFetchingPreviousPage || !hasPreviousPage) return;

    const prevCursor = data.pages[0]?.data?.prevCursor;
    if (!prevCursor) return;

    setIsFetchingPreviousPage(true);
    try {
      const response = await getActivityTimeline(lead_id, {
        limit: 20,
        type,
        includeChildLead,
        cursor: prevCursor,
        direction: "asc", // Load newer data
      });

      queryClient.setQueryData(queryKey, {
        pages: [{ data: response.data }, ...data.pages],
        pageParams: [undefined, ...Array(data.pages.length).fill(undefined)],
      });
    } finally {
      setIsFetchingPreviousPage(false);
    }
  }, [
    lead_id,
    data,
    isFetchingPreviousPage,
    hasPreviousPage,
    type,
    includeChildLead,
    queryKey,
  ]);

  const filters = useMemo(
    () => ({
      includeChildLead,
      type,
      setIncludeChildLead,
      setType,
    }),
    [includeChildLead, type]
  );

  const [deletingId, setDeletingId] = useState<number | string | undefined>();

  const {
    handleDeleteComments,
    setDialogOpenConfirm,
    dialogOpenConfirm,
    loadDeleting,
  } = useDeleteComments();

  const value: ActivityContextType = {
    filters,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    isFetching,
    isLoading,
    isError,
    forceRefetch,
    handleDeleteComments: () => {
      handleDeleteComments(deletingId);
      setDeletingId(undefined);
    },
    setDialogOpenConfirm,
    dialogOpenConfirm,
    loadDeleting,
    deletingId,
    setDeletingId,
    highlightedActivityId,
    setHighlightedActivityId,
    queryKey,
    isFindingActivity,
    setIsFindingActivity,
    findInActivityRoute,
    setFindInActivityRoute,
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};
